import {
  LinkedDocumentRef,
  LiquidityAccount,
  LiquidityAccountFormValues,
  LiquidityStatement
} from "../../create-loan/liquidity-calculator/types";

// types.ts#generateStatementId prefixes every client-created statement with
// "stmt-" purely so the form has a stable React list key while editing — the
// backend has never seen that id. Its contract is: a statement WITH an id
// updates that existing row, one WITHOUT an id is inserted as new, and any of
// the asset's existing statements left out of the array are deleted. Sending
// our client-only placeholder id back as if it were a real one would make the
// backend think a brand-new statement is an update to a row that doesn't
// exist, so it has to be stripped before the request goes out.
const isClientGeneratedStatementId = (id: string): boolean => id.startsWith("stmt-");

const toStatementRequestBody = (statement: LiquidityStatement) => {
  const { id, ...rest } = statement;
  return isClientGeneratedStatementId(id) ? rest : { id, ...rest };
};

export const toAssetRequestBody = (values: LiquidityAccountFormValues) => ({
  ...values,
  statements: values.statements.map(toStatementRequestBody)
});

const toLinkedDocument = (doc: any): LinkedDocumentRef => ({
  id: doc?.id,
  name: doc?.name ?? ""
});

const fromApiStatement = (statement: any): LiquidityStatement => ({
  id: String(statement?.id ?? ""),
  date: statement?.date ?? "",
  balance: Number(statement?.balance) || 0,
  linkedDocuments: Array.isArray(statement?.linkedDocuments)
    ? statement.linkedDocuments.map(toLinkedDocument)
    : [],
  considered: !!statement?.considered
});

// Normalizes the backend's response back into LiquidityAccount — mainly
// coercing numeric DB ids (asset_id/statement id are int4 columns) to the
// strings the frontend types use everywhere else.
export const fromApiAsset = (asset: any): LiquidityAccount => ({
  id: String(asset?.id ?? asset?.assetId ?? ""),
  loanId: String(asset?.loanId ?? ""),
  assetType: asset?.assetType,
  bank: asset?.bank ?? "",
  accountNumber: asset?.accountNumber ?? "",
  accountType: asset?.accountType,
  accountHolder: asset?.accountHolder ?? "",
  jointAccountHolder: asset?.jointAccountHolder ?? "",
  relation: asset?.relation ?? "",
  ownershipPercent: Number(asset?.ownershipPercent) || 0,
  statements: Array.isArray(asset?.statements)
    ? asset.statements.map(fromApiStatement)
    : [],
  createdOn: asset?.createdOn,
  updatedOn: asset?.updatedOn
});

// `assets` comes back as a bare array nested in the whole-loan GET response
// (/aggregate/loans/{loanId}?loanStage=...). The {data: [...]} envelope
// fallback stays as a defensive no-op in case that ever changes.
export const fromApiAssetList = (responseData: any): any[] => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
};

// Same defensive unwrap for a single-asset response (POST/PATCH).
export const fromApiAssetResponse = (responseData: any): any =>
  responseData?.data ?? responseData;
